import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API, useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import {
  RotateCcw, XCircle, ChevronLeft, ChevronRight, FileImage, Sparkles,
  CheckCircle2, Undo2, Move, PanelRightOpen, PanelRightClose, Pencil,
  Eye, EyeOff, Trash2, Send, X, Layers,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import AppTopBar from "@/components/AppTopBar";

const IMAGE_EXT = /\.(jpe?g|png|webp|bmp|gif|heic|heif|tiff?|cr2|cr3|nef|arw|dng|orf|rw2|raf)$/i;

// Formats the browser can render natively (fastest — CDN direct-hit).
// RAW / HEIC fall back to /preview which transcodes to JPEG.
const NATIVE_BROWSER_EXT = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "bmp",
]);

function previewUrl(jobId, folder, filename) {
  return `${API}/jobs/${jobId}/files/${encodeURIComponent(filename)}/preview?folder=${folder}`;
}

/** Pick the fastest URL for an image: direct CDN when the browser can
 *  render the format natively, else the backend transcode endpoint. */
function pickPreviewSrc(jobId, folder, file) {
  if (!file || !file.filename) return "";
  const name = file.filename;
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
  if (file.cdn_url && NATIVE_BROWSER_EXT.has(ext)) return file.cdn_url;
  return previewUrl(jobId, folder, name);
}

function stemLower(name) {
  return (name || "").replace(IMAGE_EXT, "").toLowerCase();
}

/**
 * Image container that
 *  • letterboxes its child to preserve aspect ratio
 *  • overlays normalized markup rectangles at the correct pixel offset
 *  • supports zoom (wheel) + pan (drag)
 *  • when ``drawMode`` is true, mouse-drag on the working surface returns
 *    a normalized rectangle via ``onDrawn(box)`` so callers can add new
 *    markup areas.
 */
function ReviewImage({
  src, boxes = [], alt, zoom = 1, pan = { x: 0, y: 0 },
  onWheel, onPan, statusById, drawMode = false, onDrawn,
  showBoxes = true,
}) {
  const wrapRef = useRef();
  const imgRef = useRef();
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [rendered, setRendered] = useState({ w: 0, h: 0, offsetX: 0, offsetY: 0 });
  const [draft, setDraft] = useState(null); // {x0,y0,x1,y1} in image-relative px
  const dragRef = useRef(null);
  const drawRef = useRef(null);

  const recomputeRendered = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img || !img.naturalWidth) return;
    const cw = wrap.clientWidth;
    const ch = wrap.clientHeight;
    const ir = img.naturalWidth / img.naturalHeight;
    const wr = cw / ch;
    let w, h, ox, oy;
    if (ir > wr) {
      w = cw; h = cw / ir; ox = 0; oy = (ch - h) / 2;
    } else {
      h = ch; w = ch * ir; oy = 0; ox = (cw - w) / 2;
    }
    setRendered({ w, h, offsetX: ox, offsetY: oy });
  }, []);

  const onLoad = (e) => {
    const el = e.target;
    setNatural({ w: el.naturalWidth, h: el.naturalHeight });
    recomputeRendered();
  };

  useEffect(() => {
    if (!wrapRef.current) return undefined;
    const ro = new ResizeObserver(() => recomputeRendered());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [recomputeRendered]);

  // Convert a client point into an image-relative fraction (0-1 both).
  const clientToNorm = (clientX, clientY) => {
    const wrap = wrapRef.current;
    if (!wrap || !rendered.w) return null;
    const rect = wrap.getBoundingClientRect();
    // Account for the zoom/pan transform so drawing works when zoomed in.
    const cx = (clientX - rect.left - rect.width / 2 - pan.x) / zoom + rect.width / 2;
    const cy = (clientY - rect.top - rect.height / 2 - pan.y) / zoom + rect.height / 2;
    const nx = (cx - rendered.offsetX) / rendered.w;
    const ny = (cy - rendered.offsetY) / rendered.h;
    return { x: Math.min(1, Math.max(0, nx)), y: Math.min(1, Math.max(0, ny)) };
  };

  const handleMouseDown = (e) => {
    if (drawMode) {
      const p = clientToNorm(e.clientX, e.clientY);
      if (!p) return;
      drawRef.current = { start: p, last: p };
      setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      e.preventDefault();
      return;
    }
    if (zoom <= 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, startPan: { ...pan } };
    e.preventDefault();
  };
  const handleMouseMove = (e) => {
    if (drawRef.current) {
      const p = clientToNorm(e.clientX, e.clientY);
      if (!p) return;
      drawRef.current.last = p;
      setDraft({
        x0: drawRef.current.start.x, y0: drawRef.current.start.y,
        x1: p.x, y1: p.y,
      });
      return;
    }
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    onPan && onPan({
      x: dragRef.current.startPan.x + dx,
      y: dragRef.current.startPan.y + dy,
    });
  };
  const handleMouseUp = () => {
    if (drawRef.current) {
      const { start, last } = drawRef.current;
      drawRef.current = null;
      setDraft(null);
      const x = Math.min(start.x, last.x);
      const y = Math.min(start.y, last.y);
      const w = Math.abs(last.x - start.x);
      const h = Math.abs(last.y - start.y);
      // Reject tiny accidental clicks.
      if (w > 0.01 && h > 0.01 && onDrawn) {
        onDrawn({ x, y, w, h });
      }
      return;
    }
    dragRef.current = null;
  };

  const draftPct = draft ? {
    x: Math.min(draft.x0, draft.x1),
    y: Math.min(draft.y0, draft.y1),
    w: Math.abs(draft.x1 - draft.x0),
    h: Math.abs(draft.y1 - draft.y0),
  } : null;

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full overflow-hidden bg-slate-100"
      style={{
        cursor: drawMode
          ? "crosshair"
          : zoom > 1
            ? (dragRef.current ? "grabbing" : "grab")
            : "default",
      }}
      onWheel={onWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        className="w-full h-full flex items-center justify-center"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "center center",
          transition: (dragRef.current || drawRef.current) ? "none" : "transform 60ms linear",
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={onLoad}
          className="max-w-full max-h-full object-contain select-none"
          draggable={false}
          style={{ imageRendering: "high-quality" }}
        />
        {showBoxes && (boxes || []).map((b, i) => {
          const st = statusById ? statusById(b, i) : "pending";
          const colour = st === "approved"
            ? "border-emerald-500 bg-emerald-500/10"
            : st === "rework"
              ? "border-red-500 bg-red-500/15"
              : "border-orange-500 bg-orange-500/10";
          const badgeColour = st === "approved"
            ? "bg-emerald-500 text-white"
            : st === "rework"
              ? "bg-red-500 text-white"
              : "bg-orange-500 text-white";
          return (
            <div
              key={i}
              className={`absolute border-2 pointer-events-none ${colour}`}
              style={{
                left: `${rendered.offsetX + b.x * rendered.w}px`,
                top: `${rendered.offsetY + b.y * rendered.h}px`,
                width: `${b.w * rendered.w}px`,
                height: `${b.h * rendered.h}px`,
              }}
            >
              <span
                className={`absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm whitespace-nowrap ${badgeColour}`}
                style={{ transform: `scale(${1 / Math.max(1, zoom)})`, transformOrigin: "bottom left" }}
              >
                #{i + 1}
                {st === "approved" && " ✓"}
                {st === "rework" && " ↻"}
              </span>
            </div>
          );
        })}
        {draftPct && (
          <div
            className="absolute border-2 border-dashed border-accent bg-accent/10 pointer-events-none"
            style={{
              left: `${rendered.offsetX + draftPct.x * rendered.w}px`,
              top: `${rendered.offsetY + draftPct.y * rendered.h}px`,
              width: `${draftPct.w * rendered.w}px`,
              height: `${draftPct.h * rendered.h}px`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function Column({ title, tone, children, empty, height = "aspect-[3/2]" }) {
  const dot = tone === "primary" ? "bg-primary"
    : tone === "accent" ? "bg-accent"
      : "bg-orange-500";
  return (
    <div className="flex flex-col min-w-0">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2 mb-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
        {title}
      </p>
      <div className={`relative ${height} bg-slate-50 border border-border rounded-md overflow-hidden`}>
        {empty ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2 p-4 text-center">
            <FileImage className="h-8 w-8" />
            <p className="text-sm font-semibold">{empty}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default function JobReviewPage() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [editedFiles, setEditedFiles] = useState([]);
  const [markups, setMarkups] = useState({});          // filename → boxes[]
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  // Compare/edit UI state
  const [showOverlay, setShowOverlay] = useState(true); // markup overlay on Original
  const [drawMode, setDrawMode] = useState(false);      // draw new markup
  const [sheetOpen, setSheetOpen] = useState(true);     // right slide-in

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, up, ed, mk] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/jobs/${jobId}/files`, { params: { folder: "uploaded" } }),
        axios.get(`${API}/jobs/${jobId}/files`, { params: { folder: "edited" } }),
        axios.get(`${API}/jobs/${jobId}/markups`),
      ]);
      setJob(j.data);
      setUploadedFiles(up.data.files || []);
      setEditedFiles(ed.data.files || []);
      const byName = {};
      for (const m of (mk.data.markups || [])) {
        byName[m.filename] = m.boxes || [];
      }
      setMarkups(byName);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      nav(`/jobs/${jobId}`);
    } finally {
      setLoading(false);
    }
  }, [jobId, nav]);

  useEffect(() => { load(); }, [load]);

  // Match each uploaded file to an edited counterpart by stem.
  // Show only photos that HAVE markups OR HAVE an edited counterpart —
  // the review UI is centred on things needing admin decisions.
  const rows = useMemo(() => {
    const edByStem = new Map();
    for (const f of editedFiles) {
      edByStem.set(stemLower(f.filename), f);
    }
    return uploadedFiles
      .filter((f) => (markups[f.filename] || []).length > 0
        || edByStem.has(stemLower(f.filename)))
      .map((f) => ({
        original: f,
        edited: edByStem.get(stemLower(f.filename)) || null,
        boxes: markups[f.filename] || [],
      }));
  }, [uploadedFiles, editedFiles, markups]);

  const setStatus = async (newStatus, confirmMsg, successMsg, redirectHome = true) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { status: newStatus });
      toast.success(successMsg);
      if (redirectHome) nav(`/jobs/${jobId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  // Shared zoom + pan state so both panes stay pixel-locked.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => Math.max(1, Math.min(20, z * dir)));
    if (dir < 1) setPan({ x: 0, y: 0 });
  }, []);
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDrawMode(false);
  }, [idx]);

  const currentRow = rows[idx] || null;
  const total = rows.length;

  // ── Markup mutations ──────────────────────────────────────────────

  const persistBoxes = async (filename, patched) => {
    try {
      await axios.put(
        `${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}`,
        { boxes: patched },
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      // Reload on error so we don't diverge silently.
      load();
    }
  };

  const setBoxReviewStatus = async (filename, boxIdx, next) => {
    const boxes = markups[filename] || [];
    const existing = boxes[boxIdx] || {};
    let review_note = existing.review_note || "";
    if (next === "rework") {
      const msg = window.prompt(
        "What should the editor rework on this area?",
        review_note || existing.note || "",
      );
      if (msg === null) return;
      review_note = msg.trim();
    } else if (next === "approved") {
      review_note = "";
    } else {
      review_note = "";
    }
    const patched = boxes.map((b, i) => (i === boxIdx ? {
      ...b,
      review_status: next,
      review_note,
      reviewed_at: new Date().toISOString(),
    } : b));
    setMarkups((m) => ({ ...m, [filename]: patched }));
    await persistBoxes(filename, patched);
    toast.success(
      next === "approved" ? `Area #${boxIdx + 1} approved`
        : next === "rework" ? `Area #${boxIdx + 1} sent back for rework`
          : `Area #${boxIdx + 1} reset`,
    );
  };

  const removeBox = async (filename, boxIdx) => {
    if (!window.confirm(`Delete area #${boxIdx + 1}?`)) return;
    const boxes = markups[filename] || [];
    const patched = boxes.filter((_, i) => i !== boxIdx);
    setMarkups((m) => ({ ...m, [filename]: patched }));
    if (patched.length === 0) {
      try {
        await axios.delete(`${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}`);
      } catch (_e) { /* 404 is fine */ }
    } else {
      await persistBoxes(filename, patched);
    }
    toast.success("Area removed");
  };

  const rejectAllBoxes = async (filename) => {
    const boxes = markups[filename] || [];
    if (boxes.length === 0) return;
    if (!window.confirm(
      `Remove ALL ${boxes.length} markup area(s) on ${filename}?`,
    )) return;
    setMarkups((m) => { const n = { ...m }; delete n[filename]; return n; });
    try {
      await axios.delete(`${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}`);
      toast.success("All markups removed");
    } catch (_e) { /* 404 is fine */ }
  };

  const handleDrawn = async (box) => {
    if (!currentRow) return;
    const label = window.prompt(
      "Short label for this new area (e.g. 'skin retouch'):",
      "",
    ) || "";
    const note = window.prompt(
      "Instruction for the editor (optional):",
      "",
    ) || "";
    const filename = currentRow.original.filename;
    const boxes = markups[filename] || [];
    const patched = [...boxes, {
      x: box.x, y: box.y, w: box.w, h: box.h,
      label: label.trim(),
      note: note.trim(),
      source: "manual",
      review_status: "rework",   // new boxes drawn in review = new rework
      review_note: note.trim(),
      reviewed_at: new Date().toISOString(),
    }];
    setMarkups((m) => ({ ...m, [filename]: patched }));
    await persistBoxes(filename, patched);
    toast.success("New markup added — send back to editor when ready");
    setDrawMode(false);
  };

  const sendBackToEditor = async () => {
    await setStatus(
      "in_progress",
      "Send this job back to the editor for another round? "
        + "All rework markups will be visible to them.",
      "Sent back for rework",
    );
  };

  const approveAndClose = async () => {
    // Check if any box is still in "rework"
    const stillRework = Object.values(markups).flat()
      .filter((b) => (b.review_status || "pending") === "rework").length;
    let confirmMsg = "Approve and close this job? "
      + "Editor loses write access.";
    if (stillRework > 0) {
      confirmMsg = `You still have ${stillRework} rework area(s) unresolved. `
        + "Close the job anyway?";
    }
    await setStatus("closed", confirmMsg, "Job closed");
  };

  if (loading || !job) {
    return (
      <div className="min-h-screen bg-background p-10">
        <p className="text-slate-500 font-medium">Loading review…</p>
      </div>
    );
  }

  const pendingCount = currentRow
    ? currentRow.boxes.filter((b) => !b.review_status || b.review_status === "pending").length
    : 0;
  const approvedCount = currentRow
    ? currentRow.boxes.filter((b) => b.review_status === "approved").length
    : 0;
  const reworkCount = currentRow
    ? currentRow.boxes.filter((b) => b.review_status === "rework").length
    : 0;

  return (
    <div className="min-h-screen bg-background text-slate-900" data-testid="job-review-page">
      <AppTopBar
        backTo={`/jobs/${jobId}`}
        eyebrow="Review"
        title={job.name}
        titleAdornment={
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${
              job.status === "closed"
                ? "bg-slate-100 text-slate-700 border-slate-300"
                : "bg-orange-50 text-orange-700 border-orange-200"
            }`}
            data-testid="review-status-pill"
          >
            {job.status === "closed" ? "Closed" : "Ready for review"}
          </span>
        }
        actions={
          isAdmin && job.status === "ready_for_review" ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setSheetOpen((s) => !s)}
                className="border-border rounded-md h-9 font-semibold gap-1.5"
                data-testid="btn-toggle-sheet"
                title={sheetOpen ? "Hide review panel" : "Show review panel"}
              >
                {sheetOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                {sheetOpen ? "Hide panel" : "Show panel"}
              </Button>
            </div>
          ) : null
        }
      />

      <main className="max-w-[1680px] mx-auto px-6 lg:px-10 py-6 space-y-5"
            style={{ paddingRight: sheetOpen ? 440 : undefined }}>
        {rows.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-10 text-center">
            <FileImage className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="font-semibold text-slate-700">
              No photos to review yet.
            </p>
          </div>
        ) : (
          <>
            {/* Phase 86: markup checklist summary — a compact stats
                bar so the admin can see at a glance how many markup
                areas across the entire job are still pending review,
                sent for rework, or approved. This is the "check
                against each markup" gate before closing a job. */}
            {(() => {
              // Aggregate box statuses across every photo.
              let approved = 0, rework = 0, pending = 0;
              const perFile = [];
              for (const r of rows) {
                const boxes = markups[r.original.filename] || [];
                let a = 0, w = 0, p = 0;
                for (const b of boxes) {
                  const st = b.review_status || "pending";
                  if (st === "approved") a += 1;
                  else if (st === "rework") w += 1;
                  else p += 1;
                }
                approved += a; rework += w; pending += p;
                if (boxes.length) perFile.push({ filename: r.original.filename, pending: p, rework: w, approved: a });
              }
              const total = approved + rework + pending;
              const decided = approved + rework;
              const pctDecided = total ? Math.round((decided / total) * 100) : 100;
              const allDone = total > 0 && pending === 0;
              const noMarkups = total === 0;
              return (
                <div
                  className="rounded-lg border border-border bg-white/70 px-4 py-3"
                  data-testid="markup-checklist-summary"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                      Markup checklist
                    </span>
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums"
                      data-testid="checklist-total"
                    >
                      <span className="text-slate-500">Total</span>
                      <span className="text-slate-900">{total}</span>
                    </span>
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] font-bold tabular-nums bg-emerald-50 border border-emerald-200 text-emerald-700"
                      data-testid="checklist-approved"
                    >
                      ✓ Approved {approved}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] font-bold tabular-nums bg-red-50 border border-red-200 text-red-700"
                      data-testid="checklist-rework"
                    >
                      ↻ Rework {rework}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] font-bold tabular-nums border ${
                        pending
                          ? "bg-orange-50 border-orange-200 text-orange-700"
                          : "bg-slate-50 border-slate-200 text-slate-500"
                      }`}
                      data-testid="checklist-pending"
                    >
                      ● Pending {pending}
                    </span>
                    <div className="flex-1 min-w-[140px] max-w-[420px]">
                      <div className="h-2 rounded-full bg-slate-200 overflow-hidden relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-emerald-500 transition-all"
                          style={{ width: `${total ? (approved / total) * 100 : 0}%` }}
                        />
                        <div
                          className="absolute inset-y-0 bg-red-500 transition-all"
                          style={{
                            left: `${total ? (approved / total) * 100 : 0}%`,
                            width: `${total ? (rework / total) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1 tabular-nums">
                        {noMarkups
                          ? "No markup areas yet — draw some on the Original pane."
                          : allDone
                            ? "All areas triaged — you can close the job or send it back to the editor."
                            : `${pctDecided}% triaged · ${pending} still to check`}
                      </p>
                    </div>
                    {allDone && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md px-2 h-7 text-[11px] font-bold uppercase tracking-widest bg-emerald-500 text-white"
                        data-testid="checklist-all-done"
                      >
                        ✓ Ready
                      </span>
                    )}
                  </div>
                  {/* Per-file breakdown — jump straight to a photo that
                      still needs work. Only rendered when at least one
                      file has active markups so the summary stays
                      compact when the admin is finished. */}
                  {perFile.length > 0 && !allDone && (
                    <div
                      className="mt-3 flex flex-wrap gap-1.5"
                      data-testid="checklist-per-file"
                    >
                      {perFile
                        .filter((f) => f.pending > 0 || f.rework > 0)
                        .map((f) => {
                          const targetIdx = rows.findIndex(
                            (r) => r.original.filename === f.filename,
                          );
                          return (
                            <button
                              key={f.filename}
                              type="button"
                              onClick={() => targetIdx >= 0 && setIdx(targetIdx)}
                              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 hover:border-primary hover:bg-primary/5 px-2 h-7 text-[11px] font-mono text-slate-700 transition-colors"
                              data-testid={`checklist-jump-${targetIdx}`}
                              title={`Jump to ${f.filename}`}
                            >
                              <span className="max-w-[160px] truncate">
                                {f.filename.split("/").pop()}
                              </span>
                              {f.pending > 0 && (
                                <span className="rounded-full bg-orange-500 text-white px-1 text-[9px]">
                                  {f.pending}
                                </span>
                              )}
                              {f.rework > 0 && (
                                <span className="rounded-full bg-red-500 text-white px-1 text-[9px]">
                                  ↻{f.rework}
                                </span>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* File selector strip */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1"
                 data-testid="file-strip">
              {rows.map((r, i) => (
                <button
                  key={r.original.filename}
                  onClick={() => setIdx(i)}
                  className={`shrink-0 px-3 h-9 rounded-md text-sm font-semibold transition-colors border ${
                    i === idx
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-white text-slate-700 border-border hover:bg-slate-50"
                  }`}
                  data-testid={`file-btn-${i}`}
                  title={r.original.filename}
                >
                  <span className="max-w-[160px] truncate inline-block align-middle">
                    {r.original.filename}
                  </span>
                  {r.boxes.length > 0 && (
                    <span className="ml-2 inline-block align-middle text-[10px] px-1.5 rounded-full bg-orange-500 text-white">
                      {r.boxes.length}
                    </span>
                  )}
                  {!r.edited && (
                    <span className="ml-2 inline-block align-middle text-[10px] px-1.5 rounded-full bg-slate-200 text-slate-700">
                      no edit
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Sub-toolbar */}
            {currentRow && (
              <div
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-white/70 px-3 py-2"
                data-testid="review-sub-toolbar"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="inline-flex items-center rounded-full px-2 h-6 text-[10px] font-bold uppercase tracking-widest bg-slate-900 text-white shrink-0">
                    {idx + 1} / {total}
                  </span>
                  <p
                    className="font-mono text-[13px] text-slate-700 truncate"
                    title={currentRow.original.filename}
                  >
                    {currentRow.original.filename}
                  </p>
                </div>

                {/* Overlay toggle */}
                <Button
                  size="sm"
                  variant={showOverlay ? "default" : "outline"}
                  onClick={() => setShowOverlay((v) => !v)}
                  className={`h-8 rounded-md text-[11px] font-bold ${
                    showOverlay ? "bg-orange-500 hover:bg-orange-600 text-white" : ""
                  }`}
                  data-testid="btn-toggle-overlay"
                  title="Toggle markup overlay on the Original pane"
                >
                  {showOverlay ? <Eye className="h-3.5 w-3.5 mr-1.5" /> : <EyeOff className="h-3.5 w-3.5 mr-1.5" />}
                  Markups {showOverlay ? "ON" : "OFF"}
                </Button>

                {/* Draw mode toggle (admin only) */}
                {isAdmin && (
                  <Button
                    size="sm"
                    variant={drawMode ? "default" : "outline"}
                    onClick={() => setDrawMode((v) => !v)}
                    className={`h-8 rounded-md text-[11px] font-bold ${
                      drawMode ? "bg-accent hover:bg-accent/90 text-accent-foreground" : "border-accent/50 text-accent hover:bg-accent/10"
                    }`}
                    data-testid="btn-draw-markup"
                    title="Drag on the Original pane to add a new markup"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    {drawMode ? "Drawing…" : "Add markup"}
                  </Button>
                )}

                <span className="font-mono tabular-nums text-[11px] text-slate-700 min-w-[48px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                {(zoom !== 1 || pan.x || pan.y) && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                    className="h-7 px-2 text-[10px] hover:bg-slate-100 rounded-sm"
                    data-testid="review-reset-zoom"
                  >
                    Reset zoom
                  </Button>
                )}

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline" size="icon"
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    className="border-border rounded-md h-8 w-8"
                    data-testid="review-prev"
                    title="Previous photo"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline" size="icon"
                    onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                    disabled={idx >= total - 1}
                    className="border-border rounded-md h-8 w-8"
                    data-testid="review-next"
                    title="Next photo"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <p className="text-[10px] text-slate-500 flex items-center gap-1 -mt-2">
              <Move className="h-3 w-3" />
              Scroll to zoom · drag to pan · both panes stay perfectly synced. Drag on Original to add new markup when Add markup is ON.
            </p>

            {/* 2-way compare (Original + toggle-able overlay) vs Edited hi-res */}
            {currentRow && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                   data-testid="two-way-grid">
                <Column
                  title={showOverlay
                    ? `Original + ${currentRow.boxes.length} markup${currentRow.boxes.length === 1 ? "" : "s"}`
                    : "Original"}
                  tone="primary"
                >
                  <ReviewImage
                    src={pickPreviewSrc(jobId, "uploaded", currentRow.original)}
                    boxes={currentRow.boxes}
                    alt="Original"
                    zoom={zoom} pan={pan}
                    onWheel={handleWheel}
                    onPan={setPan}
                    statusById={(b) => b.review_status || "pending"}
                    drawMode={drawMode}
                    onDrawn={handleDrawn}
                    showBoxes={showOverlay}
                  />
                </Column>

                <Column
                  title="Edited (high-res)"
                  tone="accent"
                  empty={
                    !currentRow.edited
                      ? "Editor hasn't uploaded an edited version yet."
                      : null
                  }
                >
                  {currentRow.edited && (
                    <ReviewImage
                      src={pickPreviewSrc(jobId, "edited", currentRow.edited)}
                      boxes={[]}
                      alt="Edited"
                      zoom={zoom} pan={pan}
                      onWheel={handleWheel}
                      onPan={setPan}
                    />
                  )}
                </Column>
              </div>
            )}
          </>
        )}
      </main>

      {/* Right slide-in review panel */}
      <Sheet open={sheetOpen && !!currentRow} onOpenChange={setSheetOpen} modal={false}>
        <SheetContent
          side="right"
          className="w-[420px] sm:max-w-[420px] flex flex-col p-0 gap-0"
          data-testid="review-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Layers className="h-4 w-4 text-accent" />
                Markup review
              </SheetTitle>
              <Button
                size="icon" variant="ghost"
                onClick={() => setSheetOpen(false)}
                className="h-7 w-7"
                data-testid="btn-sheet-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SheetDescription className="text-xs">
              Approve each area or send it back for another round. Add new
              markups via the toolbar.
            </SheetDescription>
          </SheetHeader>

          {currentRow && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Summary counts */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-orange-50 border border-orange-200 p-2 text-center">
                  <p className="text-[9px] uppercase tracking-widest font-bold text-orange-700">Pending</p>
                  <p className="text-lg font-bold text-orange-700" data-testid="count-pending">{pendingCount}</p>
                </div>
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-center">
                  <p className="text-[9px] uppercase tracking-widest font-bold text-emerald-700">Approved</p>
                  <p className="text-lg font-bold text-emerald-700" data-testid="count-approved">{approvedCount}</p>
                </div>
                <div className="rounded-md bg-red-50 border border-red-200 p-2 text-center">
                  <p className="text-[9px] uppercase tracking-widest font-bold text-red-700">Rework</p>
                  <p className="text-lg font-bold text-red-700" data-testid="count-rework">{reworkCount}</p>
                </div>
              </div>

              {/* Reject-all shortcut */}
              {isAdmin && currentRow.boxes.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => rejectAllBoxes(currentRow.original.filename)}
                  className="w-full border-red-300 text-red-700 hover:bg-red-50 hover:border-red-500 rounded-md h-8 text-[11px] font-bold uppercase tracking-widest"
                  data-testid="btn-reject-all"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Reject all markups on this photo
                </Button>
              )}

              {/* Per-box list */}
              <div className="space-y-2" data-testid="markup-review-list">
                {currentRow.boxes.length === 0 ? (
                  <div className="border border-dashed border-slate-300 rounded-md p-6 text-center">
                    <Sparkles className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                    <p className="text-xs font-semibold text-slate-500">
                      No markups on this photo.
                    </p>
                    {isAdmin && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Click <span className="font-bold">Add markup</span> in the toolbar and drag on the Original pane to create one.
                      </p>
                    )}
                  </div>
                ) : (
                  currentRow.boxes.map((b, i) => {
                    const st = b.review_status || "pending";
                    return (
                      <div
                        key={i}
                        className={`rounded-md p-3 border ${
                          st === "approved" ? "bg-emerald-50 border-emerald-200"
                            : st === "rework" ? "bg-red-50 border-red-200"
                              : "bg-white border-orange-200"
                        }`}
                        data-testid={`markup-row-${i}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm shrink-0 mt-0.5 ${
                            st === "approved" ? "bg-emerald-500 text-white"
                              : st === "rework" ? "bg-red-500 text-white"
                                : "bg-orange-500 text-white"
                          }`}>
                            #{i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 text-[13px] truncate">
                              {b.label || `Area ${i + 1}`}
                            </div>
                            {b.note && (
                              <div className="text-[11px] text-slate-600 mt-0.5">
                                {b.note}
                              </div>
                            )}
                            {b.review_note && (
                              <div className={`text-[11px] mt-1 pl-2 border-l-2 ${
                                st === "rework" ? "border-red-500 text-red-700" : "border-emerald-500 text-emerald-700"
                              }`}
                                data-testid={`review-note-${i}`}
                              >
                                <span className="font-bold uppercase tracking-widest text-[9px] mr-1">
                                  {st === "rework" ? "Rework:" : "Admin:"}
                                </span>
                                {b.review_note}
                              </div>
                            )}
                          </div>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => removeBox(currentRow.original.filename, i)}
                              className="text-slate-400 hover:text-red-600 p-0.5 rounded-sm shrink-0"
                              data-testid={`btn-remove-box-${i}`}
                              title="Remove this markup entirely"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1.5">
                            <Button
                              size="sm"
                              variant={st === "approved" ? "default" : "outline"}
                              onClick={() => setBoxReviewStatus(
                                currentRow.original.filename, i,
                                st === "approved" ? "pending" : "approved",
                              )}
                              className={`flex-1 h-7 px-2 text-[10px] rounded-sm ${
                                st === "approved"
                                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                  : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                              }`}
                              data-testid={`review-approve-${i}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {st === "approved" ? "Approved" : "Approve"}
                            </Button>
                            <Button
                              size="sm"
                              variant={st === "rework" ? "default" : "outline"}
                              onClick={() => setBoxReviewStatus(
                                currentRow.original.filename, i,
                                st === "rework" ? "pending" : "rework",
                              )}
                              className={`flex-1 h-7 px-2 text-[10px] rounded-sm ${
                                st === "rework"
                                  ? "bg-red-600 text-white hover:bg-red-700"
                                  : "border-red-500 text-red-700 hover:bg-red-50"
                              }`}
                              data-testid={`review-rework-${i}`}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              {st === "rework" ? "Reworking" : "Rework"}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Sheet footer with global actions */}
          {isAdmin && (
            <div className="border-t border-border p-4 space-y-2 bg-slate-50">
              <Button
                onClick={sendBackToEditor}
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-50 rounded-md h-9 font-bold"
                data-testid="btn-send-back-editor"
                disabled={job.status !== "ready_for_review"}
                title="Reopens the job so the editor can address the rework areas"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Send back to editor
              </Button>
              <Button
                onClick={approveAndClose}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-9 font-bold shadow-sm"
                data-testid="btn-approve-close"
                disabled={job.status !== "ready_for_review"}
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                Approve &amp; close job
              </Button>
              <p className="text-[10px] text-slate-500 text-center leading-relaxed pt-1">
                Closing the job locks all uploads. Reopen from the job page if you need another round.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
